import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import PlatformCalendar from './platformCalendar';
import { calendarOverrideStyles } from './datePickerStyles';
import { useTheme } from '../../hooks/useTheme';
import {
  applyFindFilters,
  clearFindFilters,
  removeFindFilter,
  selectFindFilterCount,
  toggleFindFilterDropdown,
} from '../../store/slices/findCandidatesSlice';

/* ── Date <-> YYYY-MM-DD ─────────────────────────────────────────────────────
   The store holds plain date strings (serialisable, and exactly what the API
   wants); the calendar works in Date objects. Formatting is done from local
   parts rather than toISOString(), which would shift the day backwards for
   anyone west of UTC and silently drop a day off the range. */
const toISODate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** The stages in use. Kept in step with STATUS_OPTIONS in FindCandidatesTable so you
 *  can never filter for a stage the table can't show, or vice versa. */
const STATUS_FILTER_OPTIONS = [
  { value: 'not_contacted', label: 'Not Contacted' },
  { value: 'email_sent', label: 'Email Sent' },
  { value: 'interested', label: 'Interested' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'not_interested', label: 'Not Interested' },
];

const statusLabel = (value) =>
  STATUS_FILTER_OPTIONS.find((o) => o.value === value)?.label ?? value;

const rangeToDates = (range) => [toDate(range?.[0]), toDate(range?.[1])];
const datesToRange = (dates) => [toISODate(dates?.[0]), toISODate(dates?.[1])];

const formatRange = (range) => {
  if (!range?.[0] && !range?.[1]) return 'Select Date Range';
  const fmt = (iso) => (iso ? toDate(iso).toLocaleDateString() : '…');
  return `${fmt(range[0])} to ${fmt(range[1])}`;
};

/** Labelled date-range picker. */
const RangeField = ({ label, hint, value, onChange }) => {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const hasValue = !!(value?.[0] || value?.[1]);

  return (
    <div className="mb-4">
      <label className="block text-sm dark:text-gray-300 text-textSecondary mb-2">{label}</label>
      <div className="relative" ref={ref}>
        <button
          className="flex items-center justify-between w-full dark:glass-effect bg-bgPrimary border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textPrimary dark:hover:bg-gray-700/50 hover:bg-gray-200 text-sm font-light h-10"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          aria-expanded={open}
        >
          <span className="truncate">{formatRange(value)}</span>
          {hasValue ? (
            <X
              className="w-4 h-4 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onChange([null, null]);
                setOpen(false);
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4 shrink-0" />
          )}
        </button>

        {open && (
          <div
            className="candidate-analytics-calendar absolute z-[100] right-0 mt-2"
            style={{
              background: theme === 'dark' ? '#0a1733' : '#fff',
              border: '1px solid #4b5563',
              borderRadius: '8px',
            }}
          >
            <div className={`platform-calendar ${theme}`}>
              <PlatformCalendar
                selectRange
                value={rangeToDates(value)}
                onChange={(dates) => {
                  onChange(datesToRange(dates));
                  if (dates?.[0] && dates?.[1]) setOpen(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
      {hint && <p className="text-xs text-gray-400 mt-1.5">{hint}</p>}
    </div>
  );
};

/**
 * Filter control for Find Candidates — the same icon + popover as the Interview
 * Management filter, but backed by findCandidatesSlice rather than filterSlice.
 *
 * It has to be its own component: filterSlice is shared with Screened Candidates and
 * Interview Management, so writing this screen's filters into it would clobber theirs
 * the same way sharing tab state would.
 */
const PANEL_W = 400;
const PANEL_H = 400;

function FindCandidatesFilter() {
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const showFilterDropdown = useSelector(
    (state) => state.findCandidatesSlice.showFilterDropdown,
  );
  const filters = useSelector((state) => state.findCandidatesSlice.filters);
  const filterCount = useSelector(selectFindFilterCount);

  // Local draft so nothing refetches until Apply is pressed.
  const [draft, setDraft] = useState(filters);
  // Fixed-viewport coords. The panel is portalled to <body> because the sub-tab bar it
  // sits in is `overflow-x-hidden` — and a box with one axis hidden and the other visible
  // computes the visible axis to `auto`, which both clipped the panel and produced a
  // stray scrollbar. Escaping the container entirely is the only clean fix.
  const [pos, setPos] = useState({ top: 0, left: 0, flipped: false });

  const place = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const flipped = window.innerHeight - r.bottom < PANEL_H + 16;
    setPos({
      top: flipped ? Math.max(8, r.top - PANEL_H - 10) : r.bottom + 10,
      // Right-align to the icon, clamped so it never runs off either edge.
      left: Math.max(8, Math.min(r.right - PANEL_W, window.innerWidth - PANEL_W - 8)),
      flipped,
    });
  }, []);

  // Re-sync every time the panel opens, so a cancelled edit doesn't linger.
  useEffect(() => {
    if (showFilterDropdown) setDraft(filters);
  }, [showFilterDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (showFilterDropdown) place();
  }, [showFilterDropdown, place]);

  useEffect(() => {
    if (!showFilterDropdown) return undefined;
    const handler = (e) => {
      if (buttonRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      dispatch(toggleFindFilterDropdown());
    };
    const onKey = (e) => {
      if (e.key === 'Escape') dispatch(toggleFindFilterDropdown());
    };
    // Anchored to a button that scrolls with the page — follow it rather than drift.
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [showFilterDropdown, dispatch, place]);

  const activeChips = [
    filters.sourcedRange?.[0] || filters.sourcedRange?.[1]
      ? { key: 'SOURCED_RANGE', label: `Sourced: ${formatRange(filters.sourcedRange)}` }
      : null,
    filters.status ? { key: 'STATUS', label: `Status: ${statusLabel(filters.status)}` } : null,
  ].filter(Boolean);

  const panel = showFilterDropdown && (
    <div
      ref={panelRef}
      style={{ top: pos.top, left: pos.left, width: PANEL_W }}
      className="fixed z-[99999] border border-gray-600 rounded-lg shadow-lg p-6 dark:bg-[#0b0b1f] bg-bgPrimary"
    >
      <style>{calendarOverrideStyles(theme)}</style>
      {/* Notch points back at the icon — below the panel when it has flipped upward. */}
      <div
        className={`absolute right-4 w-4 h-4 rotate-45 dark:bg-[#0b0b1f] bg-bgPrimary ${
          pos.flipped
            ? '-bottom-[9px] border-r border-b border-gray-600'
            : '-top-[9px] border-l border-t border-gray-600'
        }`}
      />

          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium dark:text-[#D9D9D9] text-textSecondary">Filter</h3>
            <button
              onClick={() => dispatch(toggleFindFilterDropdown())}
              className="dark:text-gray-400 text-textSecondary dark:hover:text-white hover:text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {activeChips.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                  Active Filters
                </label>
                <button
                  onClick={() => {
                    dispatch(clearFindFilters());
                    setDraft({ sourcedRange: [null, null], status: '' });
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  Clear All
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs dark:bg-gray-700/50 bg-gray-100 dark:text-gray-200 text-textSecondary"
                  >
                    {chip.label}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => {
                        dispatch(removeFindFilter(chip.key));
                        setDraft((prev) =>
                          chip.key === 'SOURCED_RANGE'
                            ? { ...prev, sourcedRange: [null, null] }
                            : { ...prev, status: '' },
                        );
                      }}
                    />
                  </span>
                ))}
              </div>
            </div>
          )}

          <RangeField
            label="Sourced Date Range"
            value={draft.sourcedRange}
            onChange={(range) => setDraft((prev) => ({ ...prev, sourcedRange: range }))}
          />

          <div className="mb-4">
            <label className="block text-sm dark:text-gray-300 text-textSecondary mb-2">
              Status
            </label>
            <div className="relative">
              <select
                value={draft.status}
                onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}
                className="appearance-none w-full dark:glass-effect bg-bgPrimary border border-gray-600 rounded-full px-4 pr-10 py-2 dark:text-gray-300 text-textPrimary dark:hover:bg-gray-700/50 hover:bg-gray-200 text-sm font-light h-10 cursor-pointer outline-none"
              >
                <option value="" className="bg-bgPrimary text-textPrimary dark:bg-gray-900 dark:text-gray-200">
                  Select a Status
                </option>
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    className="bg-bgPrimary text-textPrimary dark:bg-gray-900 dark:text-gray-200"
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              onClick={() => setDraft({ sourcedRange: [null, null], status: '' })}
              className="px-5 py-2 rounded-full text-sm font-medium dark:text-gray-300 text-textSecondary border border-gray-600 dark:hover:bg-gray-700/50 hover:bg-gray-100"
            >
              Reset
            </button>
            <button
              onClick={() => dispatch(applyFindFilters(draft))}
              className="px-6 py-2 rounded-full text-sm font-medium text-white hover:opacity-90"
              style={{ background: 'linear-gradient(to right, #ec4899, #3b82f6)' }}
            >
              Apply
            </button>
          </div>
    </div>
  );

  return (
    <div className="relative">
      <div className="relative group flex items-center">
        <button
          ref={buttonRef}
          className="relative flex items-center gap-2 dark:glass-effect border dark:border-gray-600 border-gray-300 rounded-2xl px-3 py-1 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-100 text-sm font-light h-9"
          onClick={(e) => {
            e.stopPropagation();
            dispatch(toggleFindFilterDropdown());
          }}
        >
          <SlidersHorizontal className="w-4 h-4" />
          {filterCount > 0 && (
            <span className="absolute -top-1 left-7 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
              {filterCount}
            </span>
          )}
        </button>

        <span className="pointer-events-none absolute -top-4 left-8 opacity-0 group-hover:opacity-100 transition dark:bg-gray-900 dark:text-white bg-gray-200 text-xs rounded px-2 py-1 whitespace-nowrap z-50">
          Filters
        </span>
      </div>

      {panel && createPortal(panel, document.body)}
    </div>
  );
}

export default FindCandidatesFilter;
